import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";

import { useConfirm } from "@/components/ui/confirm";
import { ApiError, api } from "@/lib/api";
import type { LoginNextResponse } from "@/types/auth";

/** The page a super admin started viewing as someone from, to go back to
 *  when they stop. Per tab. */
const RETURN_KEY = "impersonation.return_to";

/**
 * A super admin starts viewing the app as another user, read-only, then
 * full-reloads to the next URL the server returns, so the session, its
 * permissions and every cached query start over as that user.
 */
export function useStartImpersonation() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<LoginNextResponse>(`/api/admin/users/${userId}/impersonate`),
    onSuccess: (response) => {
      try {
        sessionStorage.setItem(
          RETURN_KEY,
          window.location.pathname + window.location.search,
        );
      } catch {
        // Storage blocked: exiting lands on the server's default page instead.
      }
      window.location.assign(response.next);
    },
  });
}

/**
 * A super admin's "View as user": asks first, then starts the view. Shared by
 * the Users grid's row menu and the user sheet, so both ask the same thing.
 * `unsavedChanges` warns that starting the view reloads the page without
 * saving them.
 */
export function useViewAsUser() {
  const confirm = useConfirm();
  const { mutate, isPending } = useStartImpersonation();
  const viewAs = useCallback(
    async (
      user: { id: string; display_name: string },
      { unsavedChanges = false }: { unsavedChanges?: boolean } = {},
    ) => {
      const ok = await confirm({
        title: `View as ${user.display_name}?`,
        description:
          "You'll see the app as they do: their role, permissions and data. Nothing can be changed, and the view lasts up to an hour. Use Exit view in the bar at the top to come back." +
          (unsavedChanges ? " Your unsaved changes to this user will be lost." : ""),
        confirmLabel: "View as user",
      });
      if (!ok) return;
      mutate(user.id, {
        onError: (err) =>
          toast.error(
            (err instanceof ApiError && err.asErrorBody?.message) ||
              "Couldn't view as this user.",
          ),
      });
    },
    [confirm, mutate],
  );
  return { viewAs, isPending };
}

/**
 * Stops viewing as someone: the super admin's own session comes back and the
 * tab full-reloads to the page they started from.
 */
export function useEndImpersonation() {
  return useMutation({
    mutationFn: () => api.post<LoginNextResponse>("/api/admin/impersonation/end"),
    onSuccess: (response) => {
      let next = response.next;
      try {
        next = sessionStorage.getItem(RETURN_KEY) ?? next;
        sessionStorage.removeItem(RETURN_KEY);
      } catch {
        // Storage blocked: land on the server's default page.
      }
      window.location.assign(next);
    },
  });
}
